import React, { useState } from "react";
import {
  Pencil,
  Trash,
  FilmStrip,
  YoutubeLogo,
  TiktokLogo,
  ClockCounterClockwise,
  FileText,
  LinkSimple,
  ChatCircle,
  PaperPlaneTilt,
  CaretUp,
  CaretDown,
} from "@phosphor-icons/react";
import { Facebook } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { getVideoComments, addVideoComment, getLatestComments } from "../../lib/api";
import { toast } from "sonner";
import { STAGES, formatEST } from "./constants";

const PlatformBadge = ({ uploaded, icon, label, onToggle, canEdit }) => (
  <button
    type="button"
    onClick={canEdit ? onToggle : undefined}
    className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
      uploaded
        ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
        : "bg-white/5 text-gray-500 border border-white/10"
    } ${canEdit ? "cursor-pointer hover:bg-white/10" : "cursor-default"}`}
    title={`${label}: ${uploaded ? "Uploaded" : "Pending"}`}
    data-testid={`platform-badge-${label.toLowerCase()}`}
  >
    {icon}
    <span>{uploaded ? "\u2705" : "\u23F3"}</span>
  </button>
);

const TablePlatformIcons = ({ video, onUpdate, canEdit }) => {
  const platforms = [
    { key: "uploaded_youtube", icon: <YoutubeLogo size={13} weight="fill" className="text-red-500" />, label: "YouTube" },
    { key: "uploaded_facebook", icon: <Facebook size={13} className="text-blue-500" />, label: "Facebook" },
    { key: "uploaded_tiktok", icon: <TiktokLogo size={13} weight="fill" />, label: "TikTok" },
  ];
  return (
    <div className="flex items-center gap-1" data-testid={`table-platforms-${video.id}`}>
      {platforms.map((p) => (
        <PlatformBadge
          key={p.key}
          uploaded={video[p.key]}
          icon={p.icon}
          label={p.label}
          canEdit={canEdit}
          onToggle={() => onUpdate(video.id, p.key, !video[p.key])}
        />
      ))}
    </div>
  );
};

const SORT_COLUMNS = [
  { key: "title", label: "Title" },
  { key: "type", label: "Type" },
  { key: "stage", label: "Stage" },
  { key: "priority", label: "Priority" },
  { key: "editor", label: "Editor" },
  { key: null, label: "Platforms" },
  { key: null, label: "Drive" },
  { key: "comment", label: "Comment" },
  { key: "scheduled", label: "Scheduled" },
  { key: null, label: "Actions" },
];

export const ActiveVideosTable = ({
  videos,
  selectedIds,
  onToggleSelectAll,
  onToggleSelect,
  sortConfig,
  onSort,
  canEdit,
  canDelete,
  isEditor,
  isAdmin,
  isMyVideo,
  editorColorMap,
  editors,
  getRowHighlight,
  getRowStyle,
  onInlineUpdate,
  onOpenHistory,
  onOpenEdit,
  onRequestDelete,
  latestComments,
  onRefreshLatestComments,
}) => {
  const [commentPopoverVideo, setCommentPopoverVideo] = useState(null);
  const [commentText, setCommentText] = useState("");
  const [commentHistory, setCommentHistory] = useState([]);
  const [commentLoading, setCommentLoading] = useState(false);

  const handleOpenComments = async (video) => {
    setCommentPopoverVideo(video.id);
    setCommentText("");
    setCommentLoading(true);
    try {
      const res = await getVideoComments(video.id);
      setCommentHistory(res.data || []);
    } catch {
      setCommentHistory([]);
    } finally {
      setCommentLoading(false);
    }
  };

  const handleSubmitComment = async (videoId) => {
    if (!commentText.trim()) return;
    try {
      await addVideoComment(videoId, commentText.trim());
      setCommentText("");
      const res = await getVideoComments(videoId);
      setCommentHistory(res.data || []);
      onRefreshLatestComments();
      toast.success("Comment added");
    } catch {
      toast.error("Failed to add comment");
    }
  };

  return (
    <div className="surface-card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-[#0B0C10] text-[#A0AEC0] text-sm uppercase tracking-wider">
              <th className="p-4 w-10">
                <input
                  type="checkbox"
                  checked={selectedIds.length === videos.length && videos.length > 0}
                  onChange={onToggleSelectAll}
                  className="rounded accent-cyan-500"
                  data-testid="select-all-checkbox"
                />
              </th>
              {SORT_COLUMNS.map((col) => (
                <th
                  key={col.label}
                  className={`text-left p-4 font-medium ${
                    col.key ? "cursor-pointer select-none hover:text-white transition-colors" : ""
                  }`}
                  onClick={col.key ? () => onSort(col.key) : undefined}
                  data-testid={`sort-header-${col.label.toLowerCase()}`}
                >
                  <span className="inline-flex items-center gap-1">
                    {col.label}
                    {col.key && (
                      <span className="inline-flex flex-col leading-none">
                        <CaretUp
                          size={10}
                          weight={sortConfig.key === col.key && sortConfig.direction === "asc" ? "fill" : "regular"}
                          className={sortConfig.key === col.key && sortConfig.direction === "asc" ? "text-cyan-400" : "text-gray-600"}
                        />
                        <CaretDown
                          size={10}
                          weight={sortConfig.key === col.key && sortConfig.direction === "desc" ? "fill" : "regular"}
                          className={sortConfig.key === col.key && sortConfig.direction === "desc" ? "text-cyan-400" : "text-gray-600"}
                        />
                      </span>
                    )}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {videos.map((video) => (
              <tr
                key={video.id}
                className={`table-row ${getRowHighlight(video)} ${isEditor && !isMyVideo(video) ? "opacity-40" : ""}`}
                style={getRowStyle(video)}
                data-testid={`video-row-${video.id}`}
              >
                {/* Checkbox */}
                <td className="p-4 w-10">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(video.id)}
                    onChange={() => onToggleSelect(video.id)}
                    className="rounded accent-cyan-500"
                    data-testid={`select-video-${video.id}`}
                  />
                </td>

                {/* Title */}
                <td className="p-4">
                  <div className="flex items-center gap-2">
                    <p className="text-white font-medium">{video.title}</p>
                    {video.short_required && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 font-semibold shrink-0" title="Short Required">
                        SHORT
                      </span>
                    )}
                    {video.script_url && (
                      <FileText size={14} className="text-cyan-400 shrink-0" title="Has Script" />
                    )}
                  </div>
                </td>

                {/* Type */}
                <td className="p-4">
                  <div className="flex items-center gap-1">
                    {video.is_long && <span className="text-xs px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400">Long</span>}
                    {video.is_short && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-400">
                        Short{video.shorts_count > 0 ? ` (${video.shorts_count})` : ""}
                      </span>
                    )}
                  </div>
                </td>

                {/* Stage */}
                <td className="p-4">
                  <div className="space-y-1">
                    <div className="flex items-center gap-1">
                      {canEdit && isMyVideo(video) ? (
                        <Select
                          value={video.stage}
                          onValueChange={(v) => onInlineUpdate(video.id, "stage", v)}
                        >
                          <SelectTrigger className={`w-28 h-7 text-xs badge-${video.stage} border-0 bg-transparent`} data-testid={`stage-select-${video.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#1A1A2E] border-white/10">
                            {STAGES.map((s) => (
                              <SelectItem key={s} value={s} className="text-white capitalize">{s}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className={`badge badge-${video.stage}`}>{video.stage}</span>
                      )}
                      <button
                        type="button"
                        onClick={() => onOpenHistory(video)}
                        className="p-1 rounded hover:bg-white/10 text-gray-500 hover:text-gray-300 transition-colors"
                        title="View stage history"
                        data-testid={`stage-history-btn-${video.id}`}
                      >
                        <ClockCounterClockwise size={14} />
                      </button>
                    </div>
                    {video.stage_changed_at && (
                      <span className="text-[10px] text-gray-500 select-none block" data-testid={`stage-date-${video.id}`}>
                        {formatEST(video.stage_changed_at)}
                      </span>
                    )}
                  </div>
                </td>

                {/* Priority */}
                <td className="p-4">
                  {(isAdmin || (canEdit && isMyVideo(video))) ? (
                    <select
                      value={video.priority ?? ""}
                      onChange={(e) => onInlineUpdate(video.id, "priority", e.target.value ? parseInt(e.target.value) : null)}
                      className="w-16 h-7 text-xs rounded bg-white/5 border border-white/10 text-white text-center"
                      data-testid={`priority-select-${video.id}`}
                    >
                      <option value="">-</option>
                      <option value="1">1</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                    </select>
                  ) : (
                    <span className="text-sm text-gray-300 font-medium">{video.priority ?? "-"}</span>
                  )}
                </td>

                {/* Editor */}
                <td className="p-4">
                  {isAdmin ? (
                    <Select
                      value={video.editor_id || "none"}
                      onValueChange={(v) => onInlineUpdate(video.id, "editor_id", v === "none" ? null : v)}
                    >
                      <SelectTrigger className="w-32 h-7 text-xs bg-transparent border-0 text-gray-300">
                        <SelectValue placeholder="Unassigned" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A2E] border-white/10">
                        <SelectItem value="none" className="text-gray-400">Unassigned</SelectItem>
                        {editors.map((e) => (
                          <SelectItem key={e.id} value={e.id} className="text-white">
                            <span className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: e.color || "#3B82F6" }} />
                              {e.name}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className="flex items-center gap-2 text-sm text-gray-300">
                      {video.editor_id && editorColorMap[video.editor_id] && (
                        <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: editorColorMap[video.editor_id] }} />
                      )}
                      {video.editor_name || "Unassigned"}
                    </span>
                  )}
                </td>

                {/* Platforms */}
                <td className="p-4">
                  <TablePlatformIcons video={video} onUpdate={onInlineUpdate} canEdit={canEdit} />
                </td>

                {/* Drive */}
                <td className="p-4">
                  {video.drive_folder_url ? (
                    <a
                      href={video.drive_folder_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/25 transition-colors text-xs font-medium"
                      title="Open Google Drive folder"
                      data-testid={`drive-link-${video.id}`}
                    >
                      <LinkSimple size={14} weight="bold" />
                      Drive
                    </a>
                  ) : (
                    <span
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white/5 border border-white/10 text-gray-600 cursor-not-allowed text-xs font-medium"
                      title="No Drive folder linked"
                      data-testid={`drive-link-disabled-${video.id}`}
                    >
                      <LinkSimple size={14} />
                      Drive
                    </span>
                  )}
                </td>

                {/* Comment */}
                <td className="p-4">
                  <Popover
                    open={commentPopoverVideo === video.id}
                    onOpenChange={(open) => {
                      if (open) {
                        handleOpenComments(video);
                      } else {
                        setCommentPopoverVideo(null);
                        setCommentHistory([]);
                      }
                    }}
                  >
                    <PopoverTrigger asChild>
                      <button
                        className="group flex items-center gap-1.5 text-left max-w-[180px]"
                        data-testid={`comment-trigger-${video.id}`}
                      >
                        <ChatCircle
                          size={16}
                          weight={latestComments[video.id] ? "fill" : "regular"}
                          className={latestComments[video.id] ? "text-cyan-400 shrink-0" : "text-gray-500 group-hover:text-gray-300 shrink-0"}
                        />
                        {latestComments[video.id] ? (
                          <span className="text-xs text-gray-300 truncate" title={latestComments[video.id].text}>
                            {latestComments[video.id].text}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-600 group-hover:text-gray-400">Add...</span>
                        )}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80 bg-[#1A1A2E] border-white/10 p-0" align="start" side="bottom">
                      <div className="p-3 border-b border-white/10">
                        <p className="text-xs font-medium text-gray-300">Comments — {video.title}</p>
                      </div>
                      <div className="max-h-48 overflow-y-auto p-3 space-y-3" data-testid={`comment-list-${video.id}`}>
                        {commentLoading ? (
                          <p className="text-xs text-gray-500 text-center py-2">Loading...</p>
                        ) : commentHistory.length === 0 ? (
                          <p className="text-xs text-gray-500 text-center py-2">No comments yet</p>
                        ) : (
                          commentHistory.map((c) => (
                            <div key={c.id} className="space-y-0.5">
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-medium text-cyan-400">{c.user_name}</span>
                                <span className="text-[10px] text-gray-600">{c.user_role}</span>
                                <span className="text-[10px] text-gray-600 ml-auto">{formatEST(c.created_at)}</span>
                              </div>
                              <p className="text-xs text-gray-300 leading-relaxed">{c.text}</p>
                            </div>
                          ))
                        )}
                      </div>
                      {(canEdit && isMyVideo(video)) && (
                        <div className="p-2 border-t border-white/10 flex gap-2">
                          <Input
                            value={commentText}
                            onChange={(e) => setCommentText(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") handleSubmitComment(video.id); }}
                            placeholder="Write a comment..."
                            className="h-8 text-xs bg-white/5 border-white/10 text-white placeholder:text-gray-500"
                            data-testid={`comment-input-${video.id}`}
                          />
                          <Button
                            size="sm"
                            onClick={() => handleSubmitComment(video.id)}
                            className="h-8 w-8 p-0 shrink-0 bg-cyan-600 hover:bg-cyan-500"
                            data-testid={`comment-submit-${video.id}`}
                          >
                            <PaperPlaneTilt size={14} />
                          </Button>
                        </div>
                      )}
                    </PopoverContent>
                  </Popover>
                </td>

                {/* Scheduled */}
                <td className="p-4 text-gray-400 text-sm">
                  {video.scheduled_date || "-"}
                </td>

                {/* Actions */}
                <td className="p-4">
                  <div className="flex gap-1">
                    {canEdit && isMyVideo(video) && (
                      <button
                        onClick={() => onOpenEdit(video)}
                        className="p-2 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white"
                        data-testid={`edit-video-${video.id}`}
                      >
                        <Pencil size={16} />
                      </button>
                    )}
                    {canDelete && (
                      <button
                        onClick={() => onRequestDelete(video)}
                        className="p-2 rounded-lg hover:bg-red-500/10 text-gray-400 hover:text-red-400"
                        data-testid={`delete-video-${video.id}`}
                      >
                        <Trash size={16} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {videos.length === 0 && (
        <div className="text-center py-12">
          <FilmStrip size={48} className="text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400">No videos found</p>
        </div>
      )}
    </div>
  );
};
